import type React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import type { OfferedAnswerDto } from "@/api/types";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { Radio } from "@/design-system/Radio";
import { twResolve } from "@/helpers/tw-resolve";
import { isDefined } from "@/helpers/util";
import { useTranslation } from "@/translations";

import type { SurveyRespondFormValues } from "../SurveyRespondPage";

interface SurveyRespondFormMultipleChoiceInputProps {
  questionId: string;
  offeredAnswers: OfferedAnswerDto[];
}

export function SurveyRespondFormMultipleChoiceInput({
  questionId,
  offeredAnswers,
}: SurveyRespondFormMultipleChoiceInputProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useFormContext<SurveyRespondFormValues>();

  return (
    <FormErrorWrapper name={`${questionId}.offeredAnswerId`} encircle>
      <Controller
        name={`${questionId}.offeredAnswerId`}
        control={form.control}
        rules={{
          validate: {
            required: (value) => {
              if (isDefined(value)) {
                return true;
              }

              return t("components.form.error.exactly-one-selected");
            },
          },
        }}
        render={({ field }) => (
          <SurveyRespondQuestionMultipleChoiceInput
            data-testid="survey-respond-multiple-choice-input"
            options={offeredAnswers.map((offeredAnswer: OfferedAnswerDto) => ({
              title: offeredAnswer.text,
              description: offeredAnswer.subtitle,
              value: offeredAnswer.id,
            }))}
            name={questionId}
            selectedValue={field.value}
            onChange={field.onChange}
          />
        )}
      />
    </FormErrorWrapper>
  );
}

interface SurveyRespondFormBinaryChoiceInputProps {
  questionId: string;
}

export function SurveyRespondFormBinaryChoiceInput({
  questionId,
}: SurveyRespondFormBinaryChoiceInputProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useFormContext<SurveyRespondFormValues>();

  return (
    <FormErrorWrapper name={`${questionId}.yesNo`} encircle>
      <Controller
        name={`${questionId}.yesNo`}
        control={form.control}
        rules={{
          validate: {
            required: (value) => {
              if (isDefined(value)) {
                return true;
              }

              return t("components.form.error.exactly-one-selected");
            },
          },
        }}
        render={({ field }) => {
          let selectedValue = undefined;
          if (isDefined(field.value)) {
            selectedValue = field.value ? "yes" : "no";
          }

          return (
            <SurveyRespondQuestionMultipleChoiceInput
              data-testid="survey-respond-binary-choice-input"
              options={[
                {
                  title: t("common.yes"),
                  value: "yes",
                },
                {
                  title: t("common.no"),
                  value: "no",
                },
              ]}
              name={questionId}
              selectedValue={selectedValue}
              onChange={(newValue) => {
                const isYes = newValue === "yes";

                field.onChange(isYes);
              }}
            />
          );
        }}
      />
    </FormErrorWrapper>
  );
}

type SurveyRespondQuestionMultipleChoiceInputProps = {
  options: {
    title: string;
    description?: string;
    value: string;
  }[];
  name: string;
  selectedValue: string | undefined;
  "data-testid"?: string;
  onChange: (selectedValue: string) => void;
};

function SurveyRespondQuestionMultipleChoiceInput({
  options,
  name,
  selectedValue,
  "data-testid": dataTestId,
  onChange,
}: SurveyRespondQuestionMultipleChoiceInputProps): React.ReactNode {
  return (
    <ul className="flex w-full flex-col" data-testid={dataTestId}>
      {options.map((option, index) => {
        const isFirst = index === 0;
        const isLast = index === options.length - 1;
        const isSelected = selectedValue === option.value;

        return (
          <li key={option.value}>
            <label
              className={twResolve(
                "relative flex cursor-pointer items-center gap-space-sm border border-b-0 border-border-secondary p-space-md transition-colors",
                isFirst && "rounded-t-lg",
                isLast && "rounded-b-lg border-b",
                isSelected
                  ? "border-border-brand-secondary bg-background-brand-secondary"
                  : "bg-background-default hover:bg-background-brand-light",
              )}
            >
              <Radio name={name} checked={isSelected} value={option.value} onChange={() => onChange(option.value)} />
              <span
                className={twJoin(
                  isSelected ? "text-body-emphasis text-text-default-inverse" : "text-body text-text-primary",
                )}
              >
                {option.title}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}
